import React, { useState, useRef, useEffect } from 'react';
import { useLocation, useHistory, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import entityServices from '../../core/services/entityServices';

const BLOOD_GROUPS = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];
const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const SPECIALIZATIONS = ['Panchakarma', 'Kayachikitsa', 'Shalakyatantra', 'Shalya Tantra', 'Prasuti Tantra', 'Kaumarabhritya', 'Swasthavritta'];
const THERAPY_TYPES = ['Abhyanga Massage Room', 'Shirodhara Room', 'Panchakarma Suite', 'Steam Room', 'Consultation Room', 'Kizhi Room', 'Nasyam Room'];
const COUNTRY_CODES = [
  { code: '+91', name: 'India', length: 10 },
  { code: '+1', name: 'USA/Canada', length: 10 },
  { code: '+44', name: 'UK', length: 10 },
  { code: '+971', name: 'UAE', length: 9 },
  { code: '+61', name: 'Australia', length: 9 },
  { code: '+65', name: 'Singapore', length: 8 },
];

const CONFIG = {
  patient: {
    title: 'Patient Registration',
    subtitle: 'Comprehensive patient documentation and demographic profile.',
    backPath: '/patients',
    endpoint: '/patients',
    success: 'Patient saved successfully',
    initial: {
      fname: '', mname: '', lname: '',
      phno: '', email: '', dob: '', gender: 'Male', age: '',
      bloodgroup: '', address: '', area: '', city: '', state: '', pincode: '',
      adhar: '', occupation: '', education: '', language: '', sociostatus: '',
      altPhno: '', emrPhono: '', fathern: '', mothern: '', fathernorspouse: '',
      srcRef: '', custGroupId: '', erpCustId: '', fileNo: '', ehrid: '', comments: '',
      srcRefId: null, status: 'ACTIVE', imgurl: null, countryCode: '+91',
      patientType: '', custGrpDesc: ''
    },
    sections: ['Profile Photo', 'Identity', 'Medical Details', 'Family & Contact', 'Demographics']
  },
  doctor: {
    title: 'Doctor Profile Management',
    subtitle: 'Manage professional credentials, scheduling, and contact details.',
    backPath: '/doctors',
    endpoint: '/doctors',
    success: 'Doctor profile saved successfully',
    initial: {
      first_name: '', mname: '', last_name: '',
      displaytitle: 'Dr.', department: '', qualification: '', specialization: '',
      phoneno: '', phone: '', landline: '', email: '', gender: 'Male', dob: '',
      bloodgroup: '', address: '', city: '', pincode: '', regno: '', desk: '',
      salarytype: '', type: '', working_days: [], start_time: '09:00', end_time: '18:00',
      status: 'AVAILABLE', countryCode: '+91'
    },
    sections: ['Basic Profile', 'Professional Details', 'Contact & Location', 'Schedule Settings']
  },
  room: {
    title: 'Therapy Room Configuration',
    subtitle: 'Configure room details, equipment, and service availability.',
    backPath: '/rooms',
    endpoint: '/rooms',
    success: 'Room configured successfully',
    initial: {
      roomName: '', roomCode: '', type: '', description: '',
      facilityId: '', floorId: '', floorName: '', packageServiceId: '',
      packageId: '', luxuryLevelId: '0', emergencyStatus: false,
      section: '', status: 'Active'
    },
    sections: ['Room Details', 'Advanced Config']
  },
  package: {
    title: 'New Therapy Package',
    subtitle: 'Create a treatment package and define its category.',
    backPath: '/packages',
    endpoint: '/packages',
    success: 'Package created successfully',
    initial: { name: '', description: '', category: '' },
    sections: ['Package Details']
  }
};

export default function EntityCreatePage({ type, isEdit }) {
  const history = useHistory();
  const location = useLocation();
  const { id } = useParams();
  const config = CONFIG[type];

  const [form, setForm] = useState(config.initial);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeStep, setActiveStep] = useState(0);
  const fileInputRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [cameraActive, setCameraActive] = useState(false);
  const [stream, setStream] = useState(null);
  const [packages, setPackages] = useState([]);
  const [packageServices, setPackageServices] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [doctors, setDoctors] = useState([]);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      if (type === 'room') {
        try {
          const [pkgRes, svcRes] = await Promise.all([
  entityServices.getPackages(),
  entityServices.getPackageServices()
]);

          setPackages(pkgRes.data.data || []);
          setPackageServices(svcRes.data.data || []);
        } catch (err) { console.error('Ref data failed', err); }
      }

      if (type === 'patient') {
        try {
          const [pkgRes, svcRes] = await Promise.all([
  entityServices.getPackages(),
  entityServices.getPackageServices()
]);
        } catch (err) { console.error('Failed to fetch doctors', err); }
      }

      if (isEdit && id) {
        await fetchExistingData();
        if (type === 'package') {
          await fetchPackageServices();
        }
      } else {
        setLoading(false);
      }
    };
    init();
  }, [type, isEdit, id]);

  const toNull = (val) => {
    if (val === '' || val === undefined || val === null) return null;
    return typeof val === 'string' ? val.trim() || null : val;
  };

  const toNumberOrNull = (val) => {
    if (val === '' || val === undefined || val === null) return null;
    const number = Number(val);
    return Number.isNaN(number) ? null : number;
  };

  const fetchExistingData = async () => {
    try {
      const { data } = await entityServices.get(config.endpoint, id);
      const item = data.data;
      if (type === 'patient') {
        setForm({
          fname: item.fname || '',
          mname: item.mname || '',
          lname: item.lname || '',
          phno: item.phno || '',
          email: item.email || '',
          dob: item.dob || '',
          gender: item.gender || 'Male',
          age: item.age || '',
          bloodgroup: item.bloodgroup || '',
          address: item.address || '',
          area: item.area || '',
          city: item.city || '',
          state: item.state || '',
          pincode: item.pincode || '',
          adhar: item.adhar || '',
          occupation: item.occupation || '',
          education: item.education || '',
          language: item.language || '',
          sociostatus: item.sociostatus || '',
          altPhno: item.altPhno || '',
          emrPhono: item.emrPhono || '',
          fathern: item.fathern || '',
          mothern: item.mothern || '',
          fathernorspouse: item.fathernorspouse || '',
          srcRef: item.srcRef || '',
          custGroupId: item.custGroupId || '',
          erpCustId: item.erpCustId || '',
          fileNo: item.fileNo || '',
          ehrid: item.ehrid || '',
          comments: item.comments || '',
          status: item.status || 'ACTIVE',
          imgurl: item.imgurl || null,
          patientType: '',
          custGrpDesc: ''
        });
      } else if (type === 'doctor') {
        setForm({
          first_name: item.first_name || '',
          mname: item.mname || '',
          last_name: item.last_name || '',
          displaytitle: item.displaytitle || 'Dr.',
          department: item.department || '',
          qualification: item.qualification || '',
          specialization: item.specialization || '',
          phoneno: item.phoneno || '',
          phone: item.phone || '',
          landline: item.landline || '',
          email: item.email || '',
          gender: item.gender || 'Male',
          dob: item.dob || '',
          bloodgroup: item.bloodgroup || '',
          address: item.address || '',
          city: item.city || '',
          pincode: item.pincode || '',
          regno: item.regno || '',
          desk: item.desk || '',
          salarytype: item.salarytype || '',
          type: item.type || '',
          working_days: item.working_days ? item.working_days.split(',') : [],
          start_time: item.start_time || '09:00',
          end_time: item.end_time || '18:00',
          status: item.status || 'AVAILABLE',
          imgurl: item.imgurl || null
        });
      } else if (type === 'room') {
        setForm({
          roomName: item.roomName || '',
          roomCode: item.roomCode || '',
          type: item.type || '',
          description: item.description || '',
          facilityId: item.facilityId || '',
          floorId: item.floorId || '',
          floorName: item.floorName || '',
          packageServiceId: item.packageServiceId || '',
          packageId: item.packageId || '',
          luxuryLevelId: (item.luxuryLevelId !== null && item.luxuryLevelId !== undefined) ? String(item.luxuryLevelId) : '0',
          emergencyStatus: !!item.emergencyStatus,
          section: item.section || '',
          status: item.status || 'Active'
        });
      } else if (type === 'package') {
        setForm({
          name: item.name || '',
          description: item.description || '',
          category: item.category || item.type || ''
        });
      }
    } catch (err) {
      toast.error('Failed to load data');
      history.push(config.backPath);  // ← v5
    } finally {
      setLoading(false);
    }
  };

  const fetchPackageServices = async () => {
    try {
const { data } = await entityServices.getById(type, id);
      setPackageServices((data.data || []).map(service => ({
        ...service,
        amount: service.amount ?? '',
        billable: service.billable !== false
      })));
    } catch (err) {
      toast.error('Failed to load package services');
    }
  };

  const addPackageServiceRow = () => {
    setPackageServices(prev => [
      ...prev,
      { tempId: Date.now(), name: '', amount: '', description: '', billable: true, packageId: Number(id) }
    ]);
  };

  const updatePackageServiceRow = (rowKey, field, value) => {
    setPackageServices(prev =>
      prev.map(service =>
        (service.id || service.tempId) === rowKey ? { ...service, [field]: value } : service
      )
    );
  };

  const removePackageServiceRow = async (service) => {
    if (service.id && !window.confirm('Delete this service from the package?')) return;
    try {
      if (service.id) {
await entityServices.deletePackageService(id, service.id);
        toast.success('Service deleted');
      }
      setPackageServices(prev =>
        prev.filter(item => (item.id || item.tempId) !== (service.id || service.tempId))
      );
    } catch (err) {
      toast.error('Failed to delete service');
    }
  };

  const savePackageServices = async (packageId) => {
    const servicesToSave = packageServices.filter(service => service.name?.trim());
    await Promise.all(servicesToSave.map(service => {
      const payload = {
        ...service,
        name: service.name.trim(),
        amount: Number(service.amount) || 0,
        description: service.description || '',
        billable: service.billable !== false,
        packageId: Number(packageId)
      };
      if (payload.id) return entityServices.updatePackageService(id, payload.id, payload);
      return entityServices.savePackageService(id, payload);
    }));
  };

  const set = (key, value) => {
    setForm(prev => {
      const nextState = { ...prev, [key]: value };
      if (key === 'dob' && value) {
        const birthDate = new Date(value);
        const today = new Date();
        if (!isNaN(birthDate.getTime())) {
          let computedAge = today.getFullYear() - birthDate.getFullYear();
          const monthDiff = today.getMonth() - birthDate.getMonth();
          if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
            computedAge--;
          }
          nextState.age = computedAge >= 0 ? String(computedAge) : '';
        }
      }
      return nextState;
    });
    setErrors(prev => {
      const next = { ...prev };
      delete next[key];
      if (key === 'dob') delete next['age'];
      return next;
    });
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image must be less than 2MB');
      return;
    }
    const formData = new FormData();
    formData.append('file', file);
    try {
      setUploading(true);
      const res = await entityServices.upload(file
        
      );
      set('imgurl', res.data);
      toast.success('Image uploaded successfully');
    } catch (err) {
      toast.error('Image upload failed');
    } finally {
      setUploading(false);
    }
  };

  const startCamera = async () => {
    try {
      const s = await navigator.mediaDevices.getUserMedia({ video: true });
      setStream(s);
      if (videoRef.current) videoRef.current.srcObject = s;
      setCameraActive(true);
    } catch (err) {
      toast.error('Could not access camera');
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    setCameraActive(false);
  };

  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      set('profile_pic', canvas.toDataURL('image/jpeg'));
      stopCamera();
    }
  };

  useEffect(() => {
    return () => { if (stream) stream.getTracks().forEach(t => t.stop()); };
  }, [stream]);

  const toggleDay = (day) => {
    setForm(prev => {
      const working_days = prev.working_days.includes(day)
        ? prev.working_days.filter(d => d !== day)
        : [...prev.working_days, day];
      return { ...prev, working_days };
    });
  };

  const validateStep = (step) => {
    const e = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const selectedCountry = COUNTRY_CODES.find(c => c.code === form.countryCode) || COUNTRY_CODES[0];
    const phoneRegex = new RegExp(`^\\d{${selectedCountry.length}}$`);
    const adharRegex = /^\d{12}$/;

    if (type === 'patient') {
      if (step === 1) {
        if (!form.fname?.trim()) e.fname = 'First name is required';
        if (!form.lname?.trim()) e.lname = 'Last name is required';
        if (!form.dob) e.dob = 'Date of birth is required';
        if (form.adhar && !adharRegex.test(form.adhar)) e.adhar = 'Aadhar must be exactly 12 digits';
        if (!form.gender) e.gender = 'Gender is required';
      } else if (step === 3) {
        if (!form.phno) e.phno = 'Primary phone is required';
        else if (!phoneRegex.test(form.phno)) e.phno = `Phone must be ${selectedCountry.length} digits for ${selectedCountry.name}`;
        if (form.email && !emailRegex.test(form.email)) e.email = 'Invalid email format';
      }
    } else if (type === 'doctor') {
      if (step === 0) {
        if (!form.first_name?.trim()) e.first_name = 'First name is required';
        if (!form.last_name?.trim()) e.last_name = 'Last name is required';
        if (!form.dob) e.dob = 'Date of birth is required';
      } else if (step === 1) {
        if (!form.regno?.trim()) e.regno = 'Registration number is required';
        if (!form.department) e.department = 'Department is required';
        if (!form.specialization) e.specialization = 'Specialization is required';
      } else if (step === 2) {
        if (!form.phoneno) e.phoneno = 'Primary phone is required';
        else if (!phoneRegex.test(form.phoneno)) e.phoneno = `Phone must be ${selectedCountry.length} digits for ${selectedCountry.name}`;
        if (form.email && !emailRegex.test(form.email)) e.email = 'Invalid email format';
      } else if (step === 3) {
        if (!form.working_days || form.working_days.length === 0) e.working_days = 'Please select at least one working day';
      }
    } else if (type === 'room') {
      if (!form.roomName?.trim()) e.roomName = 'Room name required';
      if (!form.type) e.type = 'Therapy type required';
    } else if (type === 'package') {
      if (!form.name?.trim()) e.name = 'Package name is required';
      if (!form.category?.trim()) e.category = 'Category is required';
      const invalidService = packageServices.find(service => service.name?.trim() && Number(service.amount) < 0);
      if (invalidService) e.packageServices = 'Service amount cannot be negative';
    }

    setErrors(e);
    if (Object.keys(e).length > 0) toast.error(Object.values(e)[0]);
    return Object.keys(e).length === 0;
  };

  const nextStep = () => {
    if (validateStep(activeStep)) {
      setActiveStep(p => p + 1);
    } else {
      toast.error('Please fill required fields');
    }
  };

  const handleSubmit = async (event) => {
    if (event) event.preventDefault();
    if (!validateStep(activeStep)) return;

    setSaving(true);
    try {
      let payload = {};
      let res;

      if (type === 'patient') {
        payload = {
          id: isEdit ? id : '',
          patientId: isEdit ? id : '',
          fname: toNull(form.fname),
          mname: toNull(form.mname),
          lname: toNull(form.lname),
          age: toNull(form.age),
          adhar: toNull(form.adhar),
          phno: toNull(form.phno),
          area: toNull(form.area),
          city: toNull(form.city),
          state: toNull(form.state),
          pincode: toNull(form.pincode),
          gender: toNull(form.gender),
          bloodgroup: toNull(form.bloodgroup),
          occupation: toNull(form.occupation),
          address: toNull(form.address),
          dob: toNull(form.dob),
          email: toNull(form.email),
          imgUrl: form.imgurl instanceof File ? '' : (form.imgurl || ''),
          udt: '',
          visit: {
            reference: { refkey: '', refby: toNull(form.srcRef) || '' },
            referenceId: toNumberOrNull(form.srcRefId) ?? null,
            visitflag: null
          },
          upload: {},
          type: form.patientType || 'OutPatient',
          insurance: 'YES',
          fileNo: toNull(form.fileNo) || '',
          srcRef: toNull(form.srcRef) || '',
          language: toNull(form.language) || '',
          education: toNull(form.education) || '',
          sociostatus: toNull(form.sociostatus) || '',
          tokenNumber: '',
          deskNo: '',
          comments: toNull(form.comments) || '',
          status: '',
          ehrid: toNull(form.ehrid) || '',
          erpCustId: toNull(form.erpCustId) || '',
          altPhno: toNull(form.altPhno) || '',
          emrPhono: toNull(form.emrPhono) || '',
          iStatus: '',
          custGroupId: toNull(form.custGroupId) || '',
          custGrpDesc: toNull(form.custGrpDesc) || '',
          mothern: toNull(form.mothern) || '',
          fathern: toNull(form.fathern) || '',
          fathernorspouse: toNull(form.fathernorspouse) || '',
          checkAdharUpdate: 'YES',
          maskAdhar: '',
          department: ''
        };

        res = isEdit
  ? await entityServices.update(config.endpoint.replace("/api/", ""), id, payload)
  : await entityServices.create(config.endpoint.replace("/api/", ""), payload);
             
            // params: { flag: 'preReg', admissionDate: '' }
            // });

      } else if (type === 'doctor') {
        payload = {
          ...form,
          working_days: form.working_days.join(','),
          name: `${form.first_name} ${form.last_name}`.trim()
        };
      } else if (type === 'room') {
        payload = {
          roomName: form.roomName,
          roomCode: form.roomCode || form.roomName.toUpperCase().replace(/\s+/g, '-'),
          type: form.type,
          description: form.description,
          facilityId: form.facilityId ? Number(form.facilityId) : null,
          floorId: form.floorId ? Number(form.floorId) : null,
          floorName: form.floorName,
          packageServiceId: form.packageServiceId ? Number(form.packageServiceId) : null,
          packageId: form.packageId ? Number(form.packageId) : null,
          luxuryLevelId: Number(form.luxuryLevelId),
          emergencyStatus: form.emergencyStatus,
          section: form.section,
          status: form.status
        };
      } else if (type === 'package') {
        payload = {
          name: form.name,
          description: form.description,
          category: form.category
        };
      }

      if (type !== 'patient') {
        res = isEdit
          ? await entityServices.update(config.endpoint.replace("/api/", ""), id, payload)
          : await entityServices.create(config.endpoint.replace("/api/", ""), payload);
      }

      if (type === 'package') {
        const packageId = res.data?.data?.id || id;
        await savePackageServices(packageId);
      }

      toast.success(
        isEdit
          ? `${type.charAt(0).toUpperCase()}${type.slice(1)} updated successfully`
          : config.success
      );

      const backendPatient = res.data?.patientresp || res.data?.data || res.data;
      const createdPatient = {
        ...payload,
        ...backendPatient,
        id: backendPatient?.id || backendPatient?.patient_id || payload.id
      };

      const returnTo = location.state?.returnTo;

      if (type === 'patient' && returnTo) {
        history.replace(returnTo, { selectedPatient: createdPatient }); // ← v5
      } else {
        history.replace(config.backPath); // ← v5
      }

    } catch (err) {
      toast.error(err.response?.data?.message || `Failed to save ${type}`);
    } finally {
      setSaving(false);
    }
  };

  const ImageUploader = () => (
    <div className="image-capture-container mb-4 text-center">
      <div className="preview-box shadow-sm mx-auto">
        <img
          src={
            form.imgurl
              ? `http://localhost:8080${form.imgurl}`
              : 'https://ui-avatars.com/api/?name=Patient&background=random'
          }
          alt="profile"
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      </div>
      <div className="mt-3 d-flex gap-2 justify-content-center">
        <input
          type="file"
          accept="image/*"
          ref={fileInputRef}
          style={{ display: 'none' }}
          onChange={handleImageUpload}
        />
        <button
          type="button"
          className="btn btn-outline-primary"
          onClick={() => fileInputRef.current.click()}
          disabled={uploading}
        >
          📁 {uploading ? 'Uploading...' : 'Upload Image'}
        </button>
        {form.imgurl && (
          <button type="button" className="btn btn-outline-danger" onClick={() => set('imgurl', null)}>
            Remove
          </button>
        )}
      </div>
    </div>
  );

  return (
    <div className="entity-create-page fade-in">
      <div className={`page-shell ${type === 'package' && isEdit ? 'page-shell-wide' : ''}`}>
        <div className="header-box mb-4">
          <button className="btn-back mb-3" onClick={() => history.push(config.backPath)}>
            ← Back to List
          </button>
          <h1 className="h3 fw-bold">{config.title}</h1>
          <p className="text-muted">{config.subtitle}</p>
        </div>

        <div className="form-card card border-0 shadow-sm rounded-4">
          <div className="card-body p-4 p-md-5">
            {config.sections.length > 1 && (
              <div className="custom-stepper mb-5">
                {config.sections.map((s, i) => (
                  <div
                    key={s}
                    className={`step-node ${activeStep === i ? 'active' : ''} ${activeStep > i ? 'done' : ''}`}
                  >
                    <div className="node-circle">{activeStep > i ? '✓' : i + 1}</div>
                    <div className="node-label">{s}</div>
                  </div>
                ))}
              </div>
            )}

            <form onSubmit={e => e.preventDefault()}>

              {/* PATIENT FORM */}
              {type === 'patient' && (
                <>
                  {activeStep === 0 && <ImageUploader />}
                  {activeStep === 1 && (
                    <div className="row g-3">
                      <div className="col-md-4">
                        <label className="form-label fw-bold">First Name *</label>
                        <input className={`form-control ${errors.fname ? 'is-invalid' : ''}`} value={form.fname} onChange={e => set('fname', e.target.value)} placeholder="Enter first name" />
                        {errors.fname && <div className="invalid-feedback">{errors.fname}</div>}
                      </div>
                      <div className="col-md-4">
                        <label className="form-label fw-bold">Middle Name</label>
                        <input className="form-control" value={form.mname} onChange={e => set('mname', e.target.value)} />
                      </div>
                      <div className="col-md-4">
                        <label className="form-label fw-bold">Last Name *</label>
                        <input className={`form-control ${errors.lname ? 'is-invalid' : ''}`} value={form.lname} onChange={e => set('lname', e.target.value)} placeholder="Enter last name" />
                        {errors.lname && <div className="invalid-feedback">{errors.lname}</div>}
                      </div>
                      <div className="col-md-4">
                        <label className="form-label fw-bold">Aadhar No</label>
                        <input className={`form-control ${errors.adhar ? 'is-invalid' : ''}`} value={form.adhar} onChange={e => set('adhar', e.target.value.replace(/\D/g, '').slice(0, 12))} placeholder="12 digit Aadhar" maxLength={12} />
                        {errors.adhar && <div className="invalid-feedback">{errors.adhar}</div>}
                      </div>
                      <div className="col-md-4">
                        <label className="form-label fw-bold">Date of Birth *</label>
                        <input className={`form-control ${errors.dob ? 'is-invalid' : ''}`} type="date" value={form.dob} max={new Date().toISOString().split('T')[0]} onChange={e => set('dob', e.target.value)} />
                        {errors.dob && <div className="invalid-feedback">{errors.dob}</div>}
                      </div>
                      <div className="col-md-4">
                        <label className="form-label fw-bold">Gender *</label>
                        <select className={`form-select ${errors.gender ? 'is-invalid' : ''}`} value={form.gender} onChange={e => set('gender', e.target.value)}>
                          <option value="">Select Gender</option>
                          <option value="Male">Male</option>
                          <option value="Female">Female</option>
                          <option value="Other">Other</option>
                        </select>
                        {errors.gender && <div className="invalid-feedback">{errors.gender}</div>}
                      </div>
                      <div className="col-md-4">
                        <label className="form-label fw-bold">Age *</label>
                        <input className={`form-control ${errors.age ? 'is-invalid' : ''}`} type="number" value={form.age} onChange={e => set('age', e.target.value)} />
                      </div>
                    </div>
                  )}
                  {activeStep === 2 && (
                    <div className="row g-3">
                      <div className="col-md-4">
                        <label className="form-label fw-bold">Patient Type</label>
                        <select className="form-select" value={form.patientType} onChange={e => set('patientType', e.target.value)}>
                          <option value="">Select Type</option>
                          <option value="In-Patient">In-Patient</option>
                          <option value="Out-Patient">Out-Patient</option>
                        </select>
                      </div>
                      <div className="col-md-4">
                        <label className="form-label fw-bold">Blood Group</label>
                        <select className="form-select" value={form.bloodgroup} onChange={e => set('bloodgroup', e.target.value)}>
                          <option value="">Select</option>
                          {BLOOD_GROUPS.map(b => <option key={b}>{b}</option>)}
                        </select>
                      </div>
                      <div className="col-md-4">
                        <label className="form-label fw-bold">Socio Status</label>
                        <input className="form-control" value={form.sociostatus} onChange={e => set('sociostatus', e.target.value)} />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label fw-bold">Education</label>
                        <input className="form-control" value={form.education} onChange={e => set('education', e.target.value)} />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label fw-bold">Occupation</label>
                        <input className="form-control" value={form.occupation} onChange={e => set('occupation', e.target.value)} />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label fw-bold">Language</label>
                        <input className="form-control" value={form.language} onChange={e => set('language', e.target.value)} />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label fw-bold">Source Reference (Doctor)</label>
                        <select
                          className="form-select"
                          value={form.srcRefId || ''}
                          onChange={e => {
                            const selected = doctors.find(d => String(d.id) === e.target.value);
                            set('srcRefId', e.target.value);
                            set('srcRef', selected ? `${selected.first_name} ${selected.last_name}` : '');
                          }}
                        >
                          <option value="">Select Doctor</option>
                          {doctors.map(doc => (
                            <option key={doc.id} value={String(doc.id)}>
                              {doc.first_name} {doc.last_name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-md-6">
                        <label className="form-label fw-bold">Customer Group</label>
                        <select className="form-select" value={form.custGrpDesc} onChange={e => {
                          const val = e.target.value;
                          set('custGrpDesc', val);
                          if (val === 'General Customer') set('custGroupId', 'G');
                          else if (val === 'Emergency Customer') set('custGroupId', 'E');
                          else set('custGroupId', '');
                        }}>
                          <option value="">Select Group</option>
                          <option value="General Customer">General Customer</option>
                          <option value="Emergency Customer">Emergency Customer</option>
                        </select>
                      </div>
                      <div className="col-md-6">
                        <label className="form-label fw-bold">Customer Group ID</label>
                        <input className="form-control" value={form.custGroupId} onChange={e => set('custGroupId', e.target.value)} disabled style={{ backgroundColor: '#e9ecef' }} />
                        <small className="text-muted">Auto-populated based on Customer Group</small>
                      </div>
                    </div>
                  )}
                  {activeStep === 3 && (
                    <div className="row g-3">
                      <div className="col-md-4">
                        <label className="form-label fw-bold">Primary Phone *</label>
                        <div className="input-group">
                          <select className="form-select border-end-0" style={{ maxWidth: '100px' }} value={form.countryCode} onChange={e => set('countryCode', e.target.value)}>
                            {COUNTRY_CODES.map(c => <option key={c.code} value={c.code}>{c.code}</option>)}
                          </select>
                          <input className={`form-control ${errors.phno ? 'is-invalid' : ''}`} value={form.phno} onChange={e => set('phno', e.target.value.replace(/\D/g, ''))} placeholder="Enter number" />
                          {errors.phno && <div className="invalid-feedback d-block">{errors.phno}</div>}
                        </div>
                      </div>
                      <div className="col-md-4">
                        <label className="form-label fw-bold">Alt Phone</label>
                        <input className="form-control" value={form.altPhno} onChange={e => set('altPhno', e.target.value)} />
                      </div>
                      <div className="col-md-4">
                        <label className="form-label fw-bold">Emergency Phone</label>
                        <input className="form-control" value={form.emrPhono} onChange={e => set('emrPhono', e.target.value)} />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label fw-bold">Email</label>
                        <input className={`form-control ${errors.email ? 'is-invalid' : ''}`} type="email" value={form.email} onChange={e => set('email', e.target.value)} />
                        {errors.email && <div className="invalid-feedback">{errors.email}</div>}
                      </div>
                      <div className="col-md-4">
                        <label className="form-label fw-bold">Father's Name</label>
                        <input className="form-control" value={form.fathern} onChange={e => set('fathern', e.target.value)} />
                      </div>
                      <div className="col-md-4">
                        <label className="form-label fw-bold">Mother's Name</label>
                        <input className="form-control" value={form.mothern} onChange={e => set('mothern', e.target.value)} />
                      </div>
                      <div className="col-md-4">
                        <label className="form-label fw-bold">Father/Spouse</label>
                        <input className="form-control" value={form.fathernorspouse} onChange={e => set('fathernorspouse', e.target.value)} />
                      </div>
                    </div>
                  )}
                  {activeStep === 4 && (
                    <div className="row g-3">
                      <div className="col-12">
                        <label className="form-label fw-bold">Full Address</label>
                        <textarea className="form-control" rows={2} value={form.address} onChange={e => set('address', e.target.value)} />
                      </div>
                      <div className="col-md-4">
                        <label className="form-label fw-bold">Area</label>
                        <input className="form-control" value={form.area} onChange={e => set('area', e.target.value)} />
                      </div>
                      <div className="col-md-4">
                        <label className="form-label fw-bold">City</label>
                        <input className="form-control" value={form.city} onChange={e => set('city', e.target.value)} />
                      </div>
                      <div className="col-md-4">
                        <label className="form-label fw-bold">State</label>
                        <input className="form-control" value={form.state} onChange={e => set('state', e.target.value)} />
                      </div>
                      <div className="col-md-4">
                        <label className="form-label fw-bold">Pincode</label>
                        <input className="form-control" value={form.pincode} onChange={e => set('pincode', e.target.value)} />
                      </div>
                      <div className="col-md-4">
                        <label className="form-label fw-bold">ERP Cust ID</label>
                        <input className="form-control" type="number" value={form.erpCustId} onChange={e => set('erpCustId', e.target.value)} />
                      </div>
                      <div className="col-md-4">
                        <label className="form-label fw-bold">File No</label>
                        <input className="form-control" value={form.fileNo} onChange={e => set('fileNo', e.target.value)} />
                      </div>
                      <div className="col-md-4">
                        <label className="form-label fw-bold">EHR ID</label>
                        <input className="form-control" value={form.ehrid} onChange={e => set('ehrid', e.target.value)} />
                      </div>
                      <div className="col-12">
                        <label className="form-label fw-bold">Comments</label>
                        <input className="form-control" value={form.comments} onChange={e => set('comments', e.target.value)} />
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* DOCTOR FORM */}
              {type === 'doctor' && (
                <>
                  {activeStep === 0 && (
                    <div className="row g-3">
                      <div className="col-md-2">
                        <label className="form-label fw-bold">Title</label>
                        <select className="form-select" value={form.displaytitle} onChange={e => set('displaytitle', e.target.value)}>
                          <option>Dr.</option><option>Mr.</option><option>Ms.</option>
                        </select>
                      </div>
                      <div className="col-md-4">
                        <label className="form-label fw-bold">First Name *</label>
                        <input className={`form-control ${errors.first_name ? 'is-invalid' : ''}`} value={form.first_name} onChange={e => set('first_name', e.target.value)} placeholder="Enter first name" />
                        {errors.first_name && <div className="invalid-feedback">{errors.first_name}</div>}
                      </div>
                      <div className="col-md-3">
                        <label className="form-label fw-bold">Middle Name</label>
                        <input className="form-control" value={form.mname} onChange={e => set('mname', e.target.value)} />
                      </div>
                      <div className="col-md-3">
                        <label className="form-label fw-bold">Last Name *</label>
                        <input className={`form-control ${errors.last_name ? 'is-invalid' : ''}`} value={form.last_name} onChange={e => set('last_name', e.target.value)} placeholder="Enter last name" />
                        {errors.last_name && <div className="invalid-feedback">{errors.last_name}</div>}
                      </div>
                      <div className="col-md-4">
                        <label className="form-label fw-bold">Date of Birth *</label>
                        <input className={`form-control ${errors.dob ? 'is-invalid' : ''}`} type="date" value={form.dob} max={new Date().toISOString().split('T')[0]} onChange={e => set('dob', e.target.value)} />
                        {errors.dob && <div className="invalid-feedback">{errors.dob}</div>}
                      </div>
                      <div className="col-md-4">
                        <label className="form-label fw-bold">Gender</label>
                        <select className="form-select" value={form.gender} onChange={e => set('gender', e.target.value)}>
                          <option>Male</option><option>Female</option><option>Other</option>
                        </select>
                      </div>
                      <div className="col-md-4">
                        <label className="form-label fw-bold">Blood Group</label>
                        <select className="form-select" value={form.bloodgroup} onChange={e => set('bloodgroup', e.target.value)}>
                          <option value="">Select</option>
                          {BLOOD_GROUPS.map(b => <option key={b}>{b}</option>)}
                        </select>
                      </div>
                    </div>
                  )}
                  {activeStep === 1 && (
                    <div className="row g-3">
                      <div className="col-md-4">
                        <label className="form-label fw-bold">Department *</label>
                        <select className={`form-select ${errors.department ? 'is-invalid' : ''}`} value={form.department} onChange={e => set('department', e.target.value)}>
                          <option value="">Select Dept</option>
                          <option>Ayurveda</option><option>Panchakarma</option>
                        </select>
                        {errors.department && <div className="invalid-feedback">{errors.department}</div>}
                      </div>
                      <div className="col-md-4">
                        <label className="form-label fw-bold">Specialization *</label>
                        <select className={`form-select ${errors.specialization ? 'is-invalid' : ''}`} value={form.specialization} onChange={e => set('specialization', e.target.value)}>
                          <option value="">Select Specialization</option>
                          {SPECIALIZATIONS.map(s => <option key={s}>{s}</option>)}
                        </select>
                        {errors.specialization && <div className="invalid-feedback">{errors.specialization}</div>}
                      </div>
                      <div className="col-md-4">
                        <label className="form-label fw-bold">Registration No *</label>
                        <input className={`form-control ${errors.regno ? 'is-invalid' : ''}`} value={form.regno} onChange={e => set('regno', e.target.value)} />
                        {errors.regno && <div className="invalid-feedback">{errors.regno}</div>}
                      </div>
                      <div className="col-md-6">
                        <label className="form-label fw-bold">Qualification</label>
                        <input className="form-control" value={form.qualification} onChange={e => set('qualification', e.target.value)} />
                      </div>
                      <div className="col-md-4">
                        <label className="form-label fw-bold">Desk</label>
                        <input className="form-control" value={form.desk} onChange={e => set('desk', e.target.value)} />
                      </div>
                      <div className="col-md-4">
                        <label className="form-label fw-bold">Salary Type</label>
                        <input className="form-control" value={form.salarytype} onChange={e => set('salarytype', e.target.value)} />
                      </div>
                      <div className="col-md-4">
                        <label className="form-label fw-bold">Type</label>
                        <input className="form-control" value={form.type} onChange={e => set('type', e.target.value)} />
                      </div>
                    </div>
                  )}
                  {activeStep === 2 && (
                    <div className="row g-3">
                      <div className="col-md-4">
                        <label className="form-label fw-bold">Primary Phone *</label>
                        <div className="input-group">
                          <select className="form-select border-end-0" style={{ maxWidth: '100px' }} value={form.countryCode} onChange={e => set('countryCode', e.target.value)}>
                            {COUNTRY_CODES.map(c => <option key={c.code} value={c.code}>{c.code}</option>)}
                          </select>
                          <input className={`form-control ${errors.phoneno ? 'is-invalid' : ''}`} value={form.phoneno} onChange={e => set('phoneno', e.target.value.replace(/\D/g, ''))} placeholder="Enter number" />
                          {errors.phoneno && <div className="invalid-feedback d-block">{errors.phoneno}</div>}
                        </div>
                      </div>
                      <div className="col-md-4">
                        <label className="form-label fw-bold">Email</label>
                        <input className={`form-control ${errors.email ? 'is-invalid' : ''}`} type="email" value={form.email} onChange={e => set('email', e.target.value)} />
                        {errors.email && <div className="invalid-feedback">{errors.email}</div>}
                      </div>
                      <div className="col-md-4">
                        <label className="form-label fw-bold">Mobile</label>
                        <input className="form-control" value={form.phone} onChange={e => set('phone', e.target.value)} />
                      </div>
                      <div className="col-md-4">
                        <label className="form-label fw-bold">Landline</label>
                        <input className="form-control" value={form.landline} onChange={e => set('landline', e.target.value)} />
                      </div>
                      <div className="col-12">
                        <label className="form-label fw-bold">Full Address</label>
                        <textarea className="form-control" rows={2} value={form.address} onChange={e => set('address', e.target.value)} />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label fw-bold">City</label>
                        <input className="form-control" value={form.city} onChange={e => set('city', e.target.value)} />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label fw-bold">Pincode</label>
                        <input className="form-control" value={form.pincode} onChange={e => set('pincode', e.target.value)} />
                      </div>
                    </div>
                  )}
                  {activeStep === 3 && (
                    <div className="row g-3">
                      <div className="col-12">
                        <label className="form-label fw-bold">Working Days *</label>
                        <div className="d-flex flex-wrap gap-2 mt-2">
                          {DAYS.map(day => (
                            <button
                              type="button"
                              key={day}
                              className={`btn btn-sm ${form.working_days.includes(day) ? 'btn-primary' : 'btn-outline-secondary'}`}
                              onClick={() => toggleDay(day)}
                            >
                              {day}
                            </button>
                          ))}
                        </div>
                        {errors.working_days && <div className="text-danger small mt-1">{errors.working_days}</div>}
                      </div>
                      <div className="col-md-6">
                        <label className="form-label fw-bold">Start Time</label>
                        <input className="form-control" type="time" value={form.start_time} onChange={e => set('start_time', e.target.value)} />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label fw-bold">End Time</label>
                        <input className="form-control" type="time" value={form.end_time} onChange={e => set('end_time', e.target.value)} />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label fw-bold">Status</label>
                        <select className="form-select" value={form.status} onChange={e => set('status', e.target.value)}>
                          <option>AVAILABLE</option><option>NOT AVAILABLE</option><option>ON LEAVE</option>
                        </select>
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* ROOM FORM */}
              {type === 'room' && (
                <>
                  {activeStep === 0 && (
                    <div className="row g-3">
                      <div className="col-md-6">
                        <label className="form-label fw-bold">Room Name *</label>
                        <input className={`form-control ${errors.roomName ? 'is-invalid' : ''}`} value={form.roomName} onChange={e => set('roomName', e.target.value)} placeholder="e.g. Deluxe Room 1" />
                        {errors.roomName && <div className="invalid-feedback">{errors.roomName}</div>}
                      </div>
                      <div className="col-md-6">
                        <label className="form-label fw-bold">Therapy Type *</label>
                        <select className={`form-select ${errors.type ? 'is-invalid' : ''}`} value={form.type} onChange={e => set('type', e.target.value)}>
                          <option value="">Select Type</option>
                          {THERAPY_TYPES.map(t => <option key={t}>{t}</option>)}
                        </select>
                        {errors.type && <div className="invalid-feedback">{errors.type}</div>}
                      </div>
                      <div className="col-md-6">
                        <label className="form-label fw-bold">Room Code (Auto if empty)</label>
                        <input className="form-control" value={form.roomCode} onChange={e => set('roomCode', e.target.value)} placeholder="e.g. RM-DEL-01" />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label fw-bold">Section / Block</label>
                        <input className="form-control" value={form.section} onChange={e => set('section', e.target.value)} placeholder="e.g. North Wing" />
                      </div>
                      <div className="col-12">
                        <label className="form-label fw-bold">Description</label>
                        <textarea className="form-control" rows={2} value={form.description} onChange={e => set('description', e.target.value)} />
                      </div>
                      <div className="col-12">
                        <label className="form-label fw-bold d-block mb-2">Room Status</label>
                        <div className="d-flex gap-4">
                          <div className="form-check">
                            <input className="form-check-input" type="radio" name="roomStatus" id="stActive" checked={form.status === 'Active'} onChange={() => set('status', 'Active')} />
                            <label className="form-check-label fw-medium" htmlFor="stActive">🟢 Active</label>
                          </div>
                          <div className="form-check">
                            <input className="form-check-input" type="radio" name="roomStatus" id="stInactive" checked={form.status === 'Inactive'} onChange={() => set('status', 'Inactive')} />
                            <label className="form-check-label fw-medium" htmlFor="stInactive">🔴 Inactive</label>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  {activeStep === 1 && (
                    <div className="row g-4">
                      <div className="col-md-4">
                        <label className="form-label fw-bold">Facility ID</label>
                        <input className="form-control" type="number" value={form.facilityId} onChange={e => set('facilityId', e.target.value)} />
                      </div>
                      <div className="col-md-4">
                        <label className="form-label fw-bold">Floor ID</label>
                        <input className="form-control" type="number" value={form.floorId} onChange={e => set('floorId', e.target.value)} />
                      </div>
                      <div className="col-md-4">
                        <label className="form-label fw-bold">Floor Name</label>
                        <input className="form-control" value={form.floorName} onChange={e => set('floorName', e.target.value)} placeholder="e.g. Ground Floor" />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label fw-bold">Luxury Level</label>
                        <select className="form-select" value={form.luxuryLevelId} onChange={e => set('luxuryLevelId', e.target.value)}>
                          <option value="0">Non AC</option>
                          <option value="1">AC</option>
                        </select>
                      </div>
                      <div className="col-md-6">
                        <label className="form-label fw-bold">Emergency Status</label>
                        <div className="form-check form-switch mt-2">
                          <input className="form-check-input" type="checkbox" checked={form.emergencyStatus} onChange={e => set('emergencyStatus', e.target.checked)} />
                          <label className="form-check-label fw-medium">{form.emergencyStatus ? '🚨 Emergency Priority' : 'Normal Room'}</label>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <label className="form-label fw-bold">Associated Package</label>
                        <select className="form-select" value={form.packageId} onChange={e => set('packageId', e.target.value)}>
                          <option value="">None / Select Package</option>
                          {packages.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                        </select>
                      </div>
                      <div className="col-md-6">
                        <label className="form-label fw-bold">Target Service</label>
                        <select className="form-select" value={form.packageServiceId} onChange={e => set('packageServiceId', e.target.value)}>
                          <option value="">None / Select Service</option>
                          {packageServices.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                        </select>
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* PACKAGE FORM */}
              {type === 'package' && (
                <div className="row g-3">
                  <div className="col-md-12">
                    <label className="form-label fw-bold">Package Name *</label>
                    <input className={`form-control ${errors.name ? 'is-invalid' : ''}`} value={form.name} onChange={e => set('name', e.target.value)} />
                    {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                  </div>
                  <div className="col-md-12">
                    <label className="form-label fw-bold">Category *</label>
                    <input className={`form-control ${errors.category ? 'is-invalid' : ''}`} value={form.category} onChange={e => set('category', e.target.value)} placeholder="e.g. Wellness, Treatment" />
                    {errors.category && <div className="invalid-feedback">{errors.category}</div>}
                  </div>
                  <div className="col-12">
                    <label className="form-label fw-bold">Description</label>
                    <textarea className="form-control" rows={3} value={form.description} onChange={e => set('description', e.target.value)} />
                  </div>
                  {isEdit && (
                    <div className="col-12 mt-4">
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <div>
                          <h2 className="h5 fw-bold mb-1">Package Services</h2>
                          <p className="text-muted mb-0 small">Update the service names, prices, billable status, and descriptions for this package.</p>
                        </div>
                        <button type="button" className="btn btn-outline-primary btn-sm" onClick={addPackageServiceRow}>
                          + Add Service
                        </button>
                      </div>
                      {errors.packageServices && <div className="text-danger small mb-2">{errors.packageServices}</div>}
                      {packageServices.length === 0 ? (
                        <div className="border rounded p-3 text-muted small">No services added yet.</div>
                      ) : (
                        <div className="table-responsive border rounded">
                          <table className="table align-middle mb-0">
                            <thead>
                              <tr>
                                <th style={{ minWidth: 180 }}>Service Name</th>
                                <th style={{ width: 140 }}>Amount (Rs)</th>
                                <th style={{ width: 120 }}>Billable</th>
                                <th style={{ minWidth: 220 }}>Description</th>
                                <th style={{ width: 90 }}></th>
                              </tr>
                            </thead>
                            <tbody>
                              {packageServices.map(service => {
                                const rowKey = service.id || service.tempId;
                                return (
                                  <tr key={rowKey}>
                                    <td>
                                      <input className="form-control" value={service.name || ''} onChange={e => updatePackageServiceRow(rowKey, 'name', e.target.value)} placeholder="Service name" />
                                    </td>
                                    <td>
                                      <input className="form-control" type="number" min="0" value={service.amount} onChange={e => updatePackageServiceRow(rowKey, 'amount', e.target.value)} />
                                    </td>
                                    <td>
                                      <div className="form-check form-switch">
                                        <input className="form-check-input" type="checkbox" checked={service.billable !== false} onChange={e => updatePackageServiceRow(rowKey, 'billable', e.target.checked)} />
                                      </div>
                                    </td>
                                    <td>
                                      <input className="form-control" value={service.description || ''} onChange={e => updatePackageServiceRow(rowKey, 'description', e.target.value)} placeholder="Optional details" />
                                    </td>
                                    <td className="text-end">
                                      <button type="button" className="btn btn-outline-danger btn-sm" onClick={() => removePackageServiceRow(service)}>Delete</button>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* FOOTER */}
              <div className="d-flex justify-content-between mt-5 pt-4 border-top">
                <button
                  type="button"
                  className="btn btn-light"
                  onClick={() => activeStep > 0 ? setActiveStep(activeStep - 1) : history.push(config.backPath)}
                >
                  {activeStep > 0 ? '← Previous' : 'Cancel'}
                </button>
                {activeStep < config.sections.length - 1 ? (
                  <button type="button" className="btn btn-primary px-4" onClick={nextStep}>
                    Next Step →
                  </button>
                ) : (
                  <button type="button" className="btn btn-primary px-5 fw-bold" onClick={handleSubmit} disabled={saving}>
                    {saving ? 'Saving...' : 'Save & Register'}
                  </button>
                )}
              </div>

            </form>
          </div>
        </div>
      </div>

      <style>{`
        .entity-create-page { padding: 40px 20px; background: #f8fafc; min-height: 100vh; }
        .page-shell { max-width: 800px; margin: 0 auto; }
        .page-shell-wide { max-width: 1080px; }
        .btn-back { background: none; border: none; color: var(--primary); font-weight: 600; cursor: pointer; padding: 0; }
        .custom-stepper { display: flex; justify-content: space-between; position: relative; }
        .custom-stepper::after { content: ''; position: absolute; top: 20px; left: 0; right: 0; height: 2px; background: #e2e8f0; z-index: 1; }
        .step-node { position: relative; z-index: 2; display: flex; flex-direction: column; align-items: center; flex: 1; text-align: center; }
        .node-circle { width: 40px; height: 40px; border-radius: 50%; background: white; border: 2px solid #e2e8f0; display: flex; align-items: center; justify-content: center; font-weight: 700; margin-bottom: 8px; transition: 0.3s; }
        .step-node.active .node-circle { background: var(--primary); color: white; border-color: var(--primary); box-shadow: 0 0 0 4px rgba(13, 148, 136, 0.1); }
        .step-node.done .node-circle { background: #10b981; color: white; border-color: #10b981; }
        .node-label { font-size: 0.75rem; font-weight: 700; color: #64748b; }
        .step-node.active .node-label { color: var(--primary); }
        .preview-box { width: 200px; height: 200px; margin: 0 auto; border-radius: 20px; overflow: hidden; background: #f1f5f9; position: relative; border: 4px solid white; }
        .camera-view { width: 100%; height: 100%; object-fit: cover; transform: scaleX(-1); }
      `}</style>
    </div>
  );
}